import {
  apiRequest,
  clearActiveOrganizationId,
  clearTabAccessToken,
  hasTabScopedSession,
  saveTabAccessToken,
} from './api';

export type AuthRole = {
  id: number;
  name: string;
  slug: string;
};

export type AuthPermission = {
  id: number;
  name: string;
  slug: string;
};

export type AuthOrganization = {
  id: number;
  name: string;
  title: string;
  subtitle?: string | null;
  logo?: string | null;
  primary_color: string;
  secondary_color: string;
  status: 'active' | 'inactive';
};

export type AuthUser = {
  id: number;
  name: string;
  email: string;
  role_id: number;
  role?: AuthRole;
  permission_ids?: number[];
  permissions?: AuthPermission[];
  organization_ids?: number[];
  organizations?: AuthOrganization[];
  is_active: boolean;
  email_verified_at?: string | null;
  last_login_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type OtpSendResult = {
  expires_in_seconds?: number;
  remaining_sends?: number;
};

export type Login2faChallenge = {
  challenge_id: string;
  email_hint: string;
  email: string;
  expires_in_seconds: number;
  remaining_sends?: number;
  emailed?: boolean;
};

export type LoginResult =
  | { requires_2fa: true; challenge: Login2faChallenge }
  | { requires_2fa: false; user: AuthUser };

const AUTH_USER_KEY = 'flipbook_auth_user';

export function saveAuthUser(user: AuthUser) {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

export function getAuthUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function clearAuthUser() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(AUTH_USER_KEY);
}

export async function login(email: string, password: string): Promise<LoginResult> {
  // Normal login uses the shared cookie — drop any tab-scoped force session.
  clearTabAccessToken();

  const result = await apiRequest<{
    requires_2fa?: boolean;
    challenge_id?: string;
    email_hint?: string;
    email?: string;
    expires_in_seconds?: number;
    remaining_sends?: number;
    emailed?: boolean;
    user?: AuthUser;
  }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  if (result.data?.requires_2fa) {
    return {
      requires_2fa: true,
      challenge: {
        challenge_id: result.data.challenge_id || '',
        email_hint: result.data.email_hint || email,
        email: result.data.email || email,
        expires_in_seconds: result.data.expires_in_seconds || 180,
        remaining_sends: result.data.remaining_sends,
        emailed: result.data.emailed,
      },
    };
  }

  const user = result.data?.user;
  if (!user) {
    throw new Error('Login succeeded but no user was returned');
  }

  saveAuthUser(user);
  return { requires_2fa: false, user };
}

export async function verifyLogin2fa(params: {
  challenge_id: string;
  email: string;
  otp: string;
}) {
  clearTabAccessToken();

  const result = await apiRequest<{ user: AuthUser }>('/api/auth/login/verify-otp', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (result.data?.user) {
    saveAuthUser(result.data.user);
  }

  return result;
}

export async function resendLogin2fa(params: {
  challenge_id: string;
  email: string;
}) {
  return apiRequest<Login2faChallenge & { requires_2fa?: boolean }>(
    '/api/auth/login/resend-otp',
    {
      method: 'POST',
      body: JSON.stringify(params),
    }
  );
}

export async function logout() {
  const tabScoped = hasTabScopedSession();
  try {
    // Tab-scoped force login must NOT clear the shared cookie (other tabs).
    if (!tabScoped) {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    }
  } finally {
    clearTabAccessToken();
    clearAuthUser();
  }
}

export async function getMe() {
  const result = await apiRequest<{ user: AuthUser }>('/api/auth/me', {
    method: 'GET',
  });

  if (result.data?.user) {
    saveAuthUser(result.data.user);
  }

  return result;
}

export async function redeemForceLogin(token: string) {
  const result = await apiRequest<{
    user: AuthUser;
    access_token?: string;
    tab_scoped?: boolean;
  }>('/api/auth/force-login', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

  const user = result.data?.user;
  const accessToken = result.data?.access_token;
  if (!user) {
    throw new Error('Force login succeeded but no user was returned');
  }
  if (!accessToken) {
    throw new Error('Force login succeeded but no access token was returned');
  }

  // Keep this tab on the force-login user only (sessionStorage).
  saveTabAccessToken(accessToken);
  clearActiveOrganizationId();
  saveAuthUser(user);
  return user;
}

export async function forgotPassword(email: string) {
  return apiRequest<OtpSendResult>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resendOtp(email: string) {
  return apiRequest<OtpSendResult>('/api/auth/resend-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyOtp(email: string, otp: string) {
  return apiRequest<{ verified: boolean }>('/api/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

export async function resetPassword(
  email: string,
  otp: string,
  password: string
) {
  return apiRequest('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, otp, password }),
  });
}
